import { useEffect, useMemo, useState } from "react";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import ClientLogoEditor from "./ClientLogoEditor";
import ExperimentoABPanel from "./ExperimentoABPanel";
import FormularioModalTabs, { FormularioModalTab } from "./FormularioModalTabs";
import type { ClientLogo } from "./clientLogo";
import FormThemeEditor from "./FormThemeEditor";
import type { FormTheme } from "./formTheme";
import { AnimatePresence, motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import CircularProgress from "@mui/material/CircularProgress";
import DropDownListCustomized from "../../ui/kit-mdtw/DropDownListCustomized";
import {
  CAMPOS,
  DOCUMENTOS,
  CAMPOS_OBLIGATORIOS_DEFAULT,
  CAMPO_API_MAP,
  DOCUMENTO_API_MAP,
  buildInitialState,
  buildDisabledState,
  normalizeParentsFromChildren,
  FieldRow,
  SectionHeader,
  labelStyle,
  MasterOptionsSelector,
  useMasterOptions,
  OpcionCampoKey,
  parseSeleccionResponse,
  buildSeleccionFields,
  CampoItem,
  CampoState,
  ModoFormulario,
} from "./CrearFormularioModal";
import {
  formatMissingOptionFieldsError,
  getEnabledOptionFieldsWithoutSelection,
} from "./formOptionValidation";
import React from "react";
import RichTextEditor from "./RichTextEditor";
import {
  JOB_OFFER_DESCRIPTION_MAX,
  DescriptionSection,
  PlainDescriptionField,
} from "./descriptionFields";
import { getUserErrorMessage, readApiErrorMessage } from "./postulacionesUtils";

// ─── types ────────────────────────────────────────────────────────────────────

interface IFormularioApi {
  contextFormId: string;
  formType: string;
  client: string;
  businessAreaId: string;
  vacancyId: string;
  recruiterId: string;
  recruiter: string;
  recruitmentSourceId?: string | null;
  recruitmentSource?: string | null;
  formCategoryId?: string | null;
  formCategory?: string | null;
  link: string;
  totalRecords: number;
  totalApplications: number;
  steps: number;
  createdAt: string;
  status?: string | null;
}

interface IFieldConfigApi {
  step: "E1" | "E2";
  field: string;
  visible: boolean;
  required: boolean;
}

interface IFormConfigApi {
  configType?: "COMPLETO" | "PASOS";
  fields?: IFieldConfigApi[];
  formDescription?: string | null;
  jobOfferDescription?: string | null;
  clientLogo?: ClientLogo | null;
  clientLogoDeferred?: boolean;
  formTheme?: FormTheme | null;
  recruitmentSourceId?: string | null;
  recruitmentSource?: string | null;
  formCategoryId?: string | null;
  formCategory?: string | null;
}

interface IFuenteReclutamiento {
  recruitmentSourceId: string;
  name: string;
}

interface ICategoriaFormulario {
  formCategoryId: string;
  name: string;
}

interface EditarFormularioModalProps {
  formulario: IFormularioApi;
  onClose: () => void;
  onSaved?: () => void;
}

// ─── reverse maps ─────────────────────────────────────────────────────────────

const API_TO_CAMPO_KEY: Record<string, string> = Object.entries(CAMPO_API_MAP)
  .filter(([, v]) => v)
  .reduce<Record<string, string>>((acc, [k, v]) => {
    acc[v as string] = k;
    return acc;
  }, {});

const API_TO_DOC_KEY: Record<string, string> = Object.entries(DOCUMENTO_API_MAP).reduce<
  Record<string, string>
>((acc, [k, v]) => {
  acc[v] = k;
  return acc;
}, {});

// ─── context label ────────────────────────────────────────────────────────────

const TIPO_CONFIG: Record<string, { bg: string; color: string; label: string }> = {
  CLIENTE:    { bg: "#ede9fe", color: "#6d28d9", label: "Cliente"    },
  CUENTA:     { bg: "#dbeafe", color: "#1d4ed8", label: "Cuenta"     },
  RECLUTADOR: { bg: "#fef3c7", color: "#b45309", label: "Reclutador" },
};

const ContextField = ({ label, value }: { label: string; value: string }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
    <span
      style={{
        fontFamily: "Roboto, sans-serif",
        fontSize: "10.5px",
        fontWeight: 600,
        color: "#94a3b8",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontFamily: "Roboto, sans-serif",
        fontSize: "13px",
        color: "#1e293b",
        fontWeight: 500,
      }}
    >
      {value || "—"}
    </span>
  </div>
);

// ─── component ────────────────────────────────────────────────────────────────

const EditarFormularioModal = ({ formulario, onClose, onSaved }: EditarFormularioModalProps) => {
  const tipoCfg = TIPO_CONFIG[formulario.formType] ?? {
    bg: "#f1f5f9", color: "#475569", label: formulario.formType || "—",
  };

  const [modoFormulario, setModoFormularioState] = useState<ModoFormulario>(
    (formulario.steps ?? 1) >= 2 ? "dividido" : "completo",
  );
  const [pasoActivo, setPasoActivo] = useState<1 | 2>(1);
  const [modalTab, setModalTab] = useState<FormularioModalTab>("configuracion");
  const [isMaximized, setIsMaximized] = useState(false);
  const [previewHost, setPreviewHost] = useState<HTMLDivElement | null>(null);

  const [campos, setCampos]         = useState<Record<string, CampoState>>(buildInitialState(CAMPOS));
  const [documentos, setDocumentos] = useState<Record<string, CampoState>>(buildInitialState(DOCUMENTOS));
  const [camposPaso2, setCamposPaso2]         = useState<Record<string, CampoState>>(buildDisabledState(CAMPOS));
  const [documentosPaso2, setDocumentosPaso2] = useState<Record<string, CampoState>>(buildDisabledState(DOCUMENTOS));

  const [hasChanges, setHasChanges]               = useState(false);
  const [showConfirmClose, setShowConfirmClose]   = useState(false);
  const [showDefaultFields, setShowDefaultFields] = useState(false);

  const [formDescEnabled, setFormDescEnabled]                 = useState(false);
  const [formDescription, setFormDescription]                 = useState("");
  const [jobOfferDescEnabled, setJobOfferDescEnabled]         = useState(false);
  const [jobOfferDescription, setJobOfferDescription]         = useState("");
  const [clientLogo, setClientLogo] = useState<ClientLogo | null>(null);
  const [formTheme, setFormTheme] = useState<FormTheme | null>(null);
  const [logoEnabled, setLogoEnabled] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoChanged, setLogoChanged] = useState(false);
  const [themeChanged, setThemeChanged] = useState(false);
  const [loadingLogo, setLoadingLogo] = useState(false);
  const [logoLoadError, setLogoLoadError] = useState<string | null>(null);

  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadError, setLoadError]         = useState<string | null>(null);
  const [saving, setSaving]               = useState(false);
  const [saveError, setSaveError]         = useState<string | null>(null);
  const [showOptionErrors, setShowOptionErrors] = useState(false);
  const [fuentesReclutamiento, setFuentesReclutamiento] = useState<IFuenteReclutamiento[]>([]);
  const [loadingFuentesReclutamiento, setLoadingFuentesReclutamiento] = useState(false);
  const [fuenteFormVals, setFuenteFormVals] = useState<any>({
    fuente: formulario.recruitmentSourceId
      ? { value: { name: formulario.recruitmentSource || "", code: formulario.recruitmentSourceId }, valid: true }
      : null,
  });
  const [categoriasFormulario, setCategoriasFormulario] = useState<ICategoriaFormulario[]>([]);
  const [loadingCategoriasFormulario, setLoadingCategoriasFormulario] = useState(false);
  const [categoriaFormVals, setCategoriaFormVals] = useState<any>({
    categoria: formulario.formCategoryId
      ? { value: { name: formulario.formCategory || "", code: formulario.formCategoryId }, valid: true }
      : null,
  });

  const markDirty = () => { if (!hasChanges) setHasChanges(true); };

  // Catálogo maestro (turno/cargo) + selección por formulario (dos niveles)
  const {
    catalogos,
    seleccion,
    setSeleccion,
    loadingCatalogos,
    addingField,
    loadCatalogos,
    toggleOption,
    addOption,
  } = useMasterOptions(markDirty);

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) return;
    setLoadingFuentesReclutamiento(true);
    fetch(`${baseUrl}/catalogs/recruitment-sources`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => setFuentesReclutamiento(
        Array.isArray(data?.recruitmentSources) ? data.recruitmentSources : [],
      ))
      .catch(() => setFuentesReclutamiento([]))
      .finally(() => setLoadingFuentesReclutamiento(false));
  }, []);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) return;
    setLoadingCategoriasFormulario(true);
    fetch(`${baseUrl}/catalogs/form-categories`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => setCategoriasFormulario(
        Array.isArray(data?.formCategories) ? data.formCategories : [],
      ))
      .catch(() => setCategoriasFormulario([]))
      .finally(() => setLoadingCategoriasFormulario(false));
  }, []);

  // Hydrate state from API
  useEffect(() => {
    let cancelled = false;
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) {
      setLoadError("Configuración faltante: REACT_APP_API_APPLICANT");
      setLoadingConfig(false);
      return;
    }
    const userId = sessionStorage.getItem("usuarioId") ?? "";
    const url =
      `${baseUrl}/form/config/${encodeURIComponent(formulario.contextFormId)}` +
      `?userId=${encodeURIComponent(userId)}&deferClientLogo=true`;

    const optionsUrl = `${baseUrl}/form/options/${encodeURIComponent(formulario.contextFormId)}`;

    // Carga el catálogo maestro global de turnos/cargos
    loadCatalogos();

    // Options and the potentially multi-megabyte logo are intentionally not on
    // the critical path. The editable fields become available as soon as the
    // lightweight configuration response arrives.
    void fetch(optionsUrl, { method: "GET", headers: { "Content-Type": "application/json" } })
      .then(async (res) => (res.ok ? await res.json() : {}))
      .catch(() => ({}))
      .then((options) => { if (!cancelled) setSeleccion(parseSeleccionResponse(options)); });

    void fetch(url, { method: "GET", headers: { "Content-Type": "application/json" } }).then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as IFormConfigApi;
      })
      .then((config) => {
        if (cancelled) return;
        applyConfig(config);
        setLoadingConfig(false);

        if (config.clientLogoDeferred) {
          setLoadingLogo(true);
          setLogoLoadError(null);
          const logoUrl =
            `${baseUrl}/form/client-logo/${encodeURIComponent(formulario.contextFormId)}` +
            `?userId=${encodeURIComponent(userId)}`;
          void fetch(logoUrl, { method: "GET", headers: { "Content-Type": "application/json" } })
            .then(async (res) => {
              if (!res.ok) throw new Error(await readApiErrorMessage(res));
              return (await res.json()) as { clientLogo?: ClientLogo | null };
            })
            .then((data) => {
              if (cancelled) return;
              setClientLogo(data.clientLogo ?? null);
              setLogoEnabled(data.clientLogo?.enabled ?? false);
              setLogoChanged(false);
            })
            .catch((error) => {
              if (!cancelled) setLogoLoadError(getUserErrorMessage(error, "No se pudo cargar la identidad visual. Puedes editar el resto del formulario sin perder el logo guardado."));
            })
            .finally(() => { if (!cancelled) setLoadingLogo(false); });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError("No se pudo cargar la configuración actual del formulario.");
        setLoadingConfig(false);
      });

    return () => { cancelled = true; };
  }, [formulario.contextFormId]);

  const applyConfig = (data: IFormConfigApi) => {
    const remoteMode: ModoFormulario =
      data?.configType === "PASOS" ? "dividido" : "completo";
    setModoFormularioState(remoteMode);

    const camposE1 = buildDisabledState(CAMPOS);
    const docsE1   = buildDisabledState(DOCUMENTOS);
    const camposE2 = buildDisabledState(CAMPOS);
    const docsE2   = buildDisabledState(DOCUMENTOS);

    (data?.fields ?? []).forEach((f) => {
      const campoKey = API_TO_CAMPO_KEY[f.field];
      const docKey   = API_TO_DOC_KEY[f.field];
      const target = f.step === "E2"
        ? { campos: camposE2, docs: docsE2 }
        : { campos: camposE1, docs: docsE1 };
      if (campoKey) {
        target.campos[campoKey] = { enabled: !!f.visible, required: !!f.required };
      } else if (docKey) {
        target.docs[docKey] = { enabled: !!f.visible, required: !!f.required };
      }
    });

    setCampos(normalizeParentsFromChildren(camposE1, CAMPOS));
    setDocumentos(docsE1);
    setCamposPaso2(normalizeParentsFromChildren(camposE2, CAMPOS));
    setDocumentosPaso2(docsE2);

    const fd = (data?.formDescription ?? "").toString();
    const jo = (data?.jobOfferDescription ?? "").toString();
    setFormDescription(fd);
    setFormDescEnabled(fd.trim() !== "");
    setJobOfferDescription(jo);
    setJobOfferDescEnabled(jo.trim() !== "");
    if (!data.clientLogoDeferred) {
      setClientLogo(data?.clientLogo ?? null);
      setLogoEnabled(data?.clientLogo?.enabled ?? false);
    }
    setLogoChanged(false);
    setFormTheme(data?.formTheme ?? null);
    setThemeChanged(false);

    setFuenteFormVals({
      fuente: data?.recruitmentSourceId
        ? {
            value: {
              name: data.recruitmentSource || formulario.recruitmentSource || "",
              code: data.recruitmentSourceId,
            },
            valid: true,
          }
        : null,
    });

    setCategoriaFormVals({
      categoria: data?.formCategoryId
        ? {
            value: {
              name: data.formCategory || formulario.formCategory || "",
              code: data.formCategoryId,
            },
            valid: true,
          }
        : null,
    });

    setHasChanges(false);
  };

  const handleRequestClose = () => {
    if (hasChanges) setShowConfirmClose(true);
    else onClose();
  };

  // Esc: cierra la confirmación si está abierta; si no, igual que la X (con
  // cambios pregunta antes). Mientras se guarda no hace nada.
  useEscapeKey(() => {
    if (showConfirmClose) { setShowConfirmClose(false); return; }
    if (saving) return;
    handleRequestClose();
  });

  // Switch mode
  const setModoFormulario = (mode: ModoFormulario) => {
    if (mode === modoFormulario) return;
    markDirty();
    setModoFormularioState(mode);
    setPasoActivo(1);
    if (mode === "completo") {
      setCamposPaso2(buildDisabledState(CAMPOS));
      setDocumentosPaso2(buildDisabledState(DOCUMENTOS));
    }
  };

  // Toggles — paso 1 / completo
  const toggleCampo = (key: string, field: "enabled" | "required") => {
    markDirty();
    const item = CAMPOS.find((c) => c.key === key);
    const newEnabled = !campos[key].enabled;

    setCampos((prev) => {
      const next = { ...prev };
      if (field === "enabled") {
        next[key] = { enabled: newEnabled, required: newEnabled ? prev[key].required : false };
        if (item?.childKeys) {
          if (newEnabled) {
            item.childKeys.forEach((ck) => { next[ck] = { ...prev[ck], enabled: true }; });
          } else {
            item.childKeys.forEach((ck) => { next[ck] = { enabled: false, required: false }; });
          }
        }
      } else if (prev[key].enabled) {
        const newRequired = !prev[key].required;
        next[key] = { ...prev[key], required: newRequired };
        item?.childKeys?.forEach((ck) => {
          if (prev[ck]?.enabled) next[ck] = { ...prev[ck], required: newRequired };
        });
      }
      return normalizeParentsFromChildren(next, CAMPOS);
    });

    if (field === "enabled" && newEnabled && modoFormulario === "dividido") {
      setCamposPaso2((prev) => {
        const next = { ...prev };
        next[key] = { enabled: false, required: false };
        if (item?.childKeys) {
          item.childKeys.forEach((ck) => { next[ck] = { enabled: false, required: false }; });
        }
        return normalizeParentsFromChildren(next, CAMPOS);
      });
    }
  };

  const toggleDocumento = (key: string, field: "enabled" | "required") => {
    markDirty();
    const newEnabled = !documentos[key].enabled;
    setDocumentos((prev) => {
      const next = { ...prev };
      if (field === "enabled") {
        next[key] = { enabled: newEnabled, required: newEnabled ? prev[key].required : false };
      } else if (prev[key].enabled) {
        next[key] = { ...prev[key], required: !prev[key].required };
      }
      return next;
    });
    if (field === "enabled" && newEnabled && modoFormulario === "dividido") {
      setDocumentosPaso2((prev) => ({ ...prev, [key]: { enabled: false, required: false } }));
    }
  };

  // Toggles — paso 2
  const toggleCampoPaso2 = (key: string, field: "enabled" | "required") => {
    markDirty();
    const item = CAMPOS.find((c) => c.key === key);
    const newEnabled = !camposPaso2[key].enabled;

    setCamposPaso2((prev) => {
      const next = { ...prev };
      if (field === "enabled") {
        next[key] = { enabled: newEnabled, required: newEnabled ? prev[key].required : false };
        if (item?.childKeys) {
          if (newEnabled) {
            item.childKeys.forEach((ck) => { next[ck] = { ...prev[ck], enabled: true }; });
          } else {
            item.childKeys.forEach((ck) => { next[ck] = { enabled: false, required: false }; });
          }
        }
      } else if (prev[key].enabled) {
        const newRequired = !prev[key].required;
        next[key] = { ...prev[key], required: newRequired };
        item?.childKeys?.forEach((ck) => {
          if (prev[ck]?.enabled) next[ck] = { ...prev[ck], required: newRequired };
        });
      }
      return normalizeParentsFromChildren(next, CAMPOS);
    });

    if (field === "enabled" && newEnabled) {
      setCampos((prev) => {
        const next = { ...prev };
        next[key] = { enabled: false, required: false };
        if (item?.childKeys) {
          item.childKeys.forEach((ck) => { next[ck] = { enabled: false, required: false }; });
        }
        return normalizeParentsFromChildren(next, CAMPOS);
      });
    }
  };

  const toggleDocumentoPaso2 = (key: string, field: "enabled" | "required") => {
    markDirty();
    const newEnabled = !documentosPaso2[key].enabled;
    setDocumentosPaso2((prev) => {
      const next = { ...prev };
      if (field === "enabled") {
        next[key] = { enabled: newEnabled, required: newEnabled ? prev[key].required : false };
      } else if (prev[key].enabled) {
        next[key] = { ...prev[key], required: !prev[key].required };
      }
      return next;
    });
    if (field === "enabled" && newEnabled) {
      setDocumentos((prev) => ({ ...prev, [key]: { enabled: false, required: false } }));
    }
  };

  // ── Active step derivations ───────────────────────────────────────────────

  const isDividido = modoFormulario === "dividido";
  const isStep1    = !isDividido || pasoActivo === 1;

  const activeCampos          = isStep1 ? campos       : camposPaso2;
  const activeDocumentos      = isStep1 ? documentos   : documentosPaso2;
  const activeToggleCampo     = isStep1 ? toggleCampo  : toggleCampoPaso2;
  const activeToggleDocumento = isStep1 ? toggleDocumento : toggleDocumentoPaso2;
  const setActiveCampos       = isStep1 ? setCampos    : setCamposPaso2;
  const setActiveDocumentos   = isStep1 ? setDocumentos : setDocumentosPaso2;

  // Keys locked by the other step
  const lockedCampoKeys = useMemo(() => {
    const s = new Set<string>();
    if (isDividido) {
      const other = pasoActivo === 1 ? camposPaso2 : campos;
      CAMPOS.forEach((c) => { if (other[c.key].enabled) s.add(c.key); });
    }
    return s;
  }, [isDividido, pasoActivo, campos, camposPaso2]);

  const lockedDocKeys = useMemo(() => {
    const s = new Set<string>();
    if (isDividido) {
      const other = pasoActivo === 1 ? documentosPaso2 : documentos;
      DOCUMENTOS.forEach((d) => { if (other[d.key].enabled) s.add(d.key); });
    }
    return s;
  }, [isDividido, pasoActivo, documentos, documentosPaso2]);

  const otherStepLabel = pasoActivo === 1 ? "En Paso 2" : "En Paso 1";

  // Bulk actions
  const unlockedCampos = CAMPOS.filter((c) => !lockedCampoKeys.has(c.key));
  const unlockedDocs   = DOCUMENTOS.filter((d) => !lockedDocKeys.has(d.key));
  const allEnabled =
    unlockedCampos.every((c) => activeCampos[c.key].enabled) &&
    unlockedDocs.every((d) => activeDocumentos[d.key].enabled);
  const enabledCampos = unlockedCampos.filter((c) => activeCampos[c.key].enabled);
  const enabledDocs   = unlockedDocs.filter((d) => activeDocumentos[d.key].enabled);
  const allRequired =
    enabledCampos.length + enabledDocs.length > 0 &&
    enabledCampos.every((c) => activeCampos[c.key].required) &&
    enabledDocs.every((d) => activeDocumentos[d.key].required);

  const toggleAllEnabled = () => {
    markDirty();
    const next = !allEnabled;
    const curCampos = activeCampos;
    const curDocs   = activeDocumentos;
    setActiveCampos(() => {
      const s: Record<string, CampoState> = {};
      CAMPOS.forEach((c) => {
        if (lockedCampoKeys.has(c.key)) {
          s[c.key] = curCampos[c.key];
        } else {
          s[c.key] = { enabled: next, required: next ? curCampos[c.key].required : false };
        }
      });
      return s;
    });
    setActiveDocumentos(() => {
      const s: Record<string, CampoState> = {};
      DOCUMENTOS.forEach((d) => {
        if (lockedDocKeys.has(d.key)) {
          s[d.key] = curDocs[d.key];
        } else {
          s[d.key] = { enabled: next, required: next ? curDocs[d.key].required : false };
        }
      });
      return s;
    });
  };

  const toggleAllRequired = () => {
    markDirty();
    const next = !allRequired;
    setActiveCampos((prev) => {
      const s: Record<string, CampoState> = {};
      CAMPOS.forEach((c) => {
        if (lockedCampoKeys.has(c.key)) {
          s[c.key] = prev[c.key];
        } else {
          s[c.key] = prev[c.key].enabled ? { ...prev[c.key], required: next } : prev[c.key];
        }
      });
      return s;
    });
    setActiveDocumentos((prev) => {
      const s: Record<string, CampoState> = {};
      DOCUMENTOS.forEach((d) => {
        if (lockedDocKeys.has(d.key)) {
          s[d.key] = prev[d.key];
        } else {
          s[d.key] = prev[d.key].enabled ? { ...prev[d.key], required: next } : prev[d.key];
        }
      });
      return s;
    });
  };

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleGuardar = async () => {
    if (logoBusy || (logoEnabled && !clientLogo)) {
      setModalTab("identidad-visual");
      setSaveError(logoBusy ? "Espera a que termine la carga o aplica/cancela la edición del logo antes de guardar." : "Carga un logo o desactiva la opción Logo del cliente.");
      return;
    }
    setSaveError(null);

    const isOptionFieldEnabled = (key: OpcionCampoKey) =>
      campos[key].enabled || (modoFormulario === "dividido" && camposPaso2[key].enabled);
    const missingOptionFields = getEnabledOptionFieldsWithoutSelection(
      seleccion,
      isOptionFieldEnabled,
    );
    setShowOptionErrors(missingOptionFields.length > 0);
    if (missingOptionFields.length > 0) {
      setSaveError(formatMissingOptionFieldsError(missingOptionFields));
      return;
    }

    setSaving(true);

    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) {
      setSaveError("Configuración faltante: REACT_APP_API_APPLICANT");
      setSaving(false);
      return;
    }
    const userId = sessionStorage.getItem("usuarioId") ?? "";

    const buildFields = (
      camposState: Record<string, CampoState>,
      docsState: Record<string, CampoState>,
      step: "E1" | "E2",
    ): IFieldConfigApi[] => {
      const fields: IFieldConfigApi[] = [];
      CAMPOS.forEach((item) => {
        const apiField = CAMPO_API_MAP[item.key];
        if (!apiField) return;
        const s = camposState[item.key];
        fields.push({ step, field: apiField, visible: s.enabled, required: s.required });
      });
      DOCUMENTOS.forEach((item) => {
        const apiField = DOCUMENTO_API_MAP[item.key];
        const s = docsState[item.key];
        fields.push({ step, field: apiField, visible: s.enabled, required: s.required });
      });
      return fields;
    };

    const allFields =
      modoFormulario === "completo"
        ? buildFields(campos, documentos, "E1")
        : [
            ...buildFields(campos, documentos, "E1"),
            ...buildFields(camposPaso2, documentosPaso2, "E2"),
          ];

    const payload = {
      configType: modoFormulario === "completo" ? "COMPLETO" : "PASOS",
      fields: allFields,
    };

    const descPayload = {
      formDescription:     formDescEnabled && formDescription.trim() !== "" ? formDescription.trim() : null,
      jobOfferDescription: jobOfferDescEnabled && jobOfferDescription.trim() !== "" ? jobOfferDescription : null,
      ...(logoChanged ? { clientLogo: clientLogo ? { ...clientLogo, enabled: logoEnabled } : null } : {}),
      ...(themeChanged ? { formTheme } : {}),
      recruitmentSourceId: fuenteFormVals.fuente?.value?.code ?? null,
      formCategoryId: categoriaFormVals.categoria?.value?.code ?? null,
    };

    const optionFields = buildSeleccionFields(
      seleccion,
      (key) => campos[key].enabled || (modoFormulario === "dividido" && camposPaso2[key].enabled),
    );

    try {
      // Guardamos primero el contexto: si la BD aún no soporta el tema/logo,
      // detenemos el flujo y evitamos disparar otros endpoints con un cambio parcial.
      const descRes = await fetch(
        `${baseUrl}/form/context/${encodeURIComponent(formulario.contextFormId)}` +
          `?userId=${encodeURIComponent(userId)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(descPayload),
        },
      );
      if (!descRes.ok) throw new Error(await readApiErrorMessage(descRes));

      const configRes = await fetch(
        `${baseUrl}/form/config/${encodeURIComponent(formulario.contextFormId)}` +
          `?userId=${encodeURIComponent(userId)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!configRes.ok) throw new Error(await readApiErrorMessage(configRes));

      if (optionFields.length > 0) {
        const optionsRes = await fetch(
          `${baseUrl}/form/options/${encodeURIComponent(formulario.contextFormId)}` +
            `?userId=${encodeURIComponent(userId)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ fields: optionFields }),
          },
        );
        if (!optionsRes.ok) throw new Error(await readApiErrorMessage(optionsRes));
      }
      onSaved?.();
      onClose();
    } catch (error) {
      setSaveError(getUserErrorMessage(error, "No se pudieron guardar los cambios. Intenta de nuevo."));
    } finally {
      setSaving(false);
    }
  };

  // ── Context summary content ────────────────────────────────────────────────
  const contextFields: { label: string; value: string }[] = [
    { label: "Tipo", value: tipoCfg.label },
  ];
  if (formulario.formType === "CLIENTE") {
    contextFields.push({ label: "Cliente", value: formulario.client });
  } else if (formulario.formType === "CUENTA") {
    contextFields.push({ label: "Cliente", value: formulario.client });
    contextFields.push({ label: "Área de negocio", value: formulario.businessAreaId });
  } else if (formulario.formType === "RECLUTADOR") {
    contextFields.push({ label: "Reclutador", value: formulario.recruiter || formulario.recruiterId });
    contextFields.push({ label: "Vacante", value: formulario.vacancyId });
  }

  const splitIdentityLayout = isMaximized && modalTab === "identidad-visual";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        backdropFilter: "blur(5px)",
        WebkitBackdropFilter: "blur(5px)",
        zIndex: 1200,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: isMaximized ? 0 : "32px 24px 48px",
        overflowY: "auto",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 18, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: isMaximized ? "100vw" : modalTab === "identidad-visual" ? "1040px" : "820px",
          height: isMaximized ? "100vh" : undefined,
          borderRadius: isMaximized ? 0 : "18px",
          overflow: "hidden",
          background: "#fff",
          boxShadow: "0 28px 64px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.07)",
          transition: "max-width 0.2s ease",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        {/* ── Header ── */}
        <div
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
            padding: "22px 24px",
            position: "relative",
          }}
        >
          <button
            type="button"
            aria-label={isMaximized ? "Salir de pantalla completa" : "Maximizar modal"}
            title={isMaximized ? "Salir de pantalla completa" : "Maximizar"}
            onClick={() => setIsMaximized((value) => !value)}
            style={{ position:"absolute", top:"14px", right:"48px", width:"28px", height:"28px", borderRadius:"50%", border:"none", background:"transparent", cursor:"pointer", color:"rgba(255,255,255,.7)", display:"flex", alignItems:"center", justifyContent:"center", padding:0 }}
          >{isMaximized ? <FullscreenExitRoundedIcon style={{fontSize:19}} /> : <FullscreenRoundedIcon style={{fontSize:19}} />}</button>
          <button
            onClick={handleRequestClose}
            style={{
              position: "absolute", top: "14px", right: "14px",
              width: "28px", height: "28px", borderRadius: "50%",
              border: "none", background: "transparent",
              cursor: "pointer", color: "rgba(255,255,255,0.6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, lineHeight: 1, outline: "none",
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#fff"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.6)"; }}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
          <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "16px", fontWeight: 600, color: "#fff" }}>
            Editar formulario
          </div>
          <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "rgba(255,255,255,0.5)", marginTop: "3px" }}>
            Modifica los campos del formulario. El tipo, cliente y reclutador no se pueden cambiar una vez creado el formulario.
          </div>
        </div>

        {splitIdentityLayout && (
          <div
            ref={setPreviewHost}
            className="client-logo-preview-host"
            role="region"
            aria-label="Previsualización del formulario"
          />
        )}

        <div style={{ width: splitIdentityLayout ? "clamp(440px, 30vw, 580px)" : "100%", flexShrink: 0 }}>
          <FormularioModalTabs
            active={modalTab}
            onChange={setModalTab}
            logoEnabled={logoEnabled}
            logoLoading={loadingLogo}
            themeCustomized={Boolean(formTheme)}
          />
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "24px", width: splitIdentityLayout ? "clamp(440px, 30vw, 580px)" : "100%", boxSizing: "border-box", maxHeight: isMaximized ? "none" : "72vh", flex: isMaximized ? "1 1 auto" : undefined, minHeight: isMaximized ? 0 : undefined, overflowY: "auto" }}>
          {/* Context (read-only) */}
          <div
            style={{
              marginBottom: "20px",
              padding: "14px 16px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              display: "flex",
              flexWrap: "wrap",
              gap: "22px",
              alignItems: "center",
            }}
          >
            <span
              style={{
                display: "inline-flex", alignItems: "center", padding: "4px 12px",
                borderRadius: "14px", background: tipoCfg.bg, color: tipoCfg.color,
                fontSize: "11.5px", fontWeight: 600,
                fontFamily: "Roboto, sans-serif", whiteSpace: "nowrap",
              }}
            >
              {tipoCfg.label}
            </span>
            {contextFields.slice(1).map((cf) => (
              <ContextField key={cf.label} label={cf.label} value={cf.value} />
            ))}
          </div>

          {loadingConfig ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", padding: "48px 0" }}>
              <CircularProgress size={28} style={{ color: "#0065DD" }} />
              <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#64748b" }}>
                Cargando configuración…
              </span>
            </div>
          ) : loadError ? (
            <div
              style={{
                padding: "16px",
                borderRadius: "10px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                color: "#b91c1c",
                textAlign: "center",
              }}
            >
              {loadError}
            </div>
          ) : modalTab === "identidad-visual" ? (
            <div
              role="tabpanel"
              id="form-modal-panel-identidad-visual"
              aria-labelledby="form-modal-tab-identidad-visual"
            >
              {loadingLogo ? (
                <><FormThemeEditor value={formTheme} onChange={(theme) => { markDirty(); setThemeChanged(true); setFormTheme(theme); }} /><div style={{ marginBottom: "16px", padding: "18px", display: "flex", alignItems: "center", gap: "10px", border: "1px solid #dbe5f0", borderRadius: "12px", background: "#f8fafc", color: "#64748b", fontFamily: "Roboto, sans-serif", fontSize: "12px" }}>
                  <CircularProgress size={18} style={{ color: "#0065DD" }} /> Cargando identidad visual…
                </div></>
              ) : logoLoadError ? (
                <><FormThemeEditor value={formTheme} onChange={(theme) => { markDirty(); setThemeChanged(true); setFormTheme(theme); }} /><div role="alert" style={{ marginBottom: "16px", padding: "12px", border: "1px solid #fde68a", borderRadius: "10px", background: "#fffbeb", color: "#92400e", fontFamily: "Roboto, sans-serif", fontSize: "12px", lineHeight: 1.45 }}>
                  {logoLoadError}
                </div></>
              ) : (
                <ClientLogoEditor value={clientLogo} enabled={logoEnabled} clientName={formulario.client} contextFormId={formulario.contextFormId}
                  formTheme={formTheme}
                  expanded={isMaximized}
                  previewHost={previewHost}
                  themeControls={<FormThemeEditor value={formTheme} onChange={(theme) => { markDirty(); setThemeChanged(true); setFormTheme(theme); }} />}
                  showJobOfferDetail={jobOfferDescEnabled} onBusyChange={setLogoBusy}
                  onToggle={() => { markDirty(); setLogoChanged(true); setLogoEnabled((enabled) => !enabled); }}
                  onChange={(logo) => { markDirty(); setLogoChanged(true); setClientLogo(logo); if (!logo) setLogoEnabled(false); }} />
              )}
            </div>
          ) : (
            <div
              role="tabpanel"
              id="form-modal-panel-configuracion"
              aria-labelledby="form-modal-tab-configuracion"
            >
              <div style={{ marginBottom: "22px" }}>
                <DropDownListCustomized
                  key={`edit-categoria-${categoriaFormVals.categoria?.value?.code ?? "empty"}`}
                  list={categoriasFormulario.map((categoria) => ({ name: categoria.name, code: categoria.formCategoryId }))}
                  layer="Categoría de formulario"
                  summary="(opcional)"
                  required={false}
                  errorPosition="down"
                  onValueChange={(vals: any) => { markDirty(); setCategoriaFormVals(vals); }}
                  onPermaValues={categoriaFormVals}
                  onPropertyChange="categoria"
                  defaultValue={categoriaFormVals.categoria?.value}
                  disabled={loadingCategoriasFormulario}
                  placeholder={loadingCategoriasFormulario ? "Cargando categorías..." : "Sin categoría de formulario"}
                />
              </div>

              <div style={{ marginBottom: "22px" }}>
                <DropDownListCustomized
                  key={`edit-fuente-${fuenteFormVals.fuente?.value?.code ?? "empty"}`}
                  list={fuentesReclutamiento.map((fuente) => ({ name: fuente.name, code: fuente.recruitmentSourceId }))}
                  layer="Fuente de reclutamiento"
                  summary="(opcional)"
                  required={false}
                  errorPosition="down"
                  onValueChange={(vals: any) => { markDirty(); setFuenteFormVals(vals); }}
                  onPermaValues={fuenteFormVals}
                  onPropertyChange="fuente"
                  defaultValue={fuenteFormVals.fuente?.value}
                  disabled={loadingFuentesReclutamiento}
                  placeholder={loadingFuentesReclutamiento ? "Cargando fuentes..." : "Sin fuente de reclutamiento"}
                />
              </div>

              {/* Descripciones opcionales */}
              <DescriptionSection
                title="Descripción del formulario"
                hint="Nota interna sobre el objetivo o propósito del formulario. No se muestra al postulante."
                enabled={formDescEnabled}
                onToggleEnabled={() => { markDirty(); setFormDescEnabled((v) => !v); }}
              >
                <PlainDescriptionField
                  value={formDescription}
                  onChange={(v) => { markDirty(); setFormDescription(v); }}
                />
              </DescriptionSection>

              <DescriptionSection
                title="Descripción de oferta laboral"
                hint="Detalle de la oferta visible para el postulante antes y durante la postulación."
                enabled={jobOfferDescEnabled}
                onToggleEnabled={() => { markDirty(); setJobOfferDescEnabled((v) => !v); }}
              >
                <RichTextEditor
                  value={jobOfferDescription}
                  onChange={(html) => { markDirty(); setJobOfferDescription(html); }}
                  maxLength={JOB_OFFER_DESCRIPTION_MAX}
                  placeholder="Describe la vacante: responsabilidades, requisitos, beneficios…"
                  minHeight={180}
                />
              </DescriptionSection>

              <div style={{ marginBottom: "22px" }}>
                <div style={labelStyle}>Modo de formulario</div>
                <div style={{ display: "flex", gap: "8px" }}>
                  {(["completo", "dividido"] as ModoFormulario[]).map((m) => {
                    const active = modoFormulario === m;
                    return (
                      <button
                        key={m}
                        onClick={() => setModoFormulario(m)}
                        onMouseEnter={(e) => {
                          if (!active) {
                            const btn = e.currentTarget as HTMLButtonElement;
                            btn.style.background = "rgba(0,101,221,0.06)";
                            btn.style.border = "1px solid #0065DD";
                            btn.style.color = "#0065DD";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!active) {
                            const btn = e.currentTarget as HTMLButtonElement;
                            btn.style.background = "#fff";
                            btn.style.border = "1px solid #e2e8f0";
                            btn.style.color = "#475569";
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: "10px 12px",
                          borderRadius: "10px",
                          border: active ? "none" : "1px solid #e2e8f0",
                          background: active
                            ? "linear-gradient(135deg, #0050b3 0%, #0065DD 100%)"
                            : "#fff",
                          color: active ? "#fff" : "#475569",
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "13px",
                          fontWeight: active ? 600 : 400,
                          cursor: "pointer",
                          boxShadow: active
                            ? "0 2px 10px rgba(0,101,221,0.3)"
                            : "0 1px 3px rgba(0,0,0,0.06)",
                          transition: "all 0.15s",
                        }}
                      >
                        {m === "completo" ? "Formulario completo" : "Formulario por pasos"}
                      </button>
                    );
                  })}
                </div>
              </div>

              <ExperimentoABPanel contextFormId={formulario.contextFormId} isDividido={isDividido} />

              {/* Step tabs */}
              <AnimatePresence>
                {isDividido && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                    animate={{ opacity: 1, height: "auto", marginBottom: 16 }}
                    exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ overflow: "hidden" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        padding: "3px",
                      }}
                    >
                      {([1, 2] as (1 | 2)[]).map((paso) => {
                        const active = pasoActivo === paso;
                        const stepCampos = paso === 1 ? campos : camposPaso2;
                        const stepDocs   = paso === 1 ? documentos : documentosPaso2;
                        const count =
                          CAMPOS.filter((c) => stepCampos[c.key].enabled).length +
                          DOCUMENTOS.filter((d) => stepDocs[d.key].enabled).length;

                        return (
                          <button
                            key={paso}
                            onClick={() => setPasoActivo(paso)}
                            style={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "7px",
                              padding: "8px 14px",
                              borderRadius: "8px",
                              border: "none",
                              background: active ? "#fff" : "transparent",
                              color: active ? "#0065DD" : "#64748b",
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "13px",
                              fontWeight: active ? 600 : 400,
                              cursor: "pointer",
                              boxShadow: active ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                              transition: "all 0.15s",
                            }}
                          >
                            Paso {paso}
                            {count > 0 && (
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  background: active ? "rgba(0,101,221,0.1)" : "#e2e8f0",
                                  color: active ? "#0065DD" : "#64748b",
                                  padding: "1px 6px",
                                  borderRadius: "10px",
                                  minWidth: "20px",
                                  textAlign: "center",
                                }}
                              >
                                {count}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <div
                      style={{
                        marginTop: "8px",
                        padding: "10px 12px",
                        borderRadius: "9px",
                        background: "rgba(0,101,221,0.05)",
                        border: "1px solid rgba(0,101,221,0.14)",
                        display: "flex",
                        gap: "9px",
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          width: "16px", height: "16px", borderRadius: "50%",
                          background: "#0065DD", color: "#fff",
                          fontSize: "10px", fontWeight: 700,
                          fontFamily: "Roboto, sans-serif",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0, marginTop: "1px",
                        }}
                      >
                        i
                      </div>
                      <div>
                        <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "12px", fontWeight: 600, color: "#0050b3", marginBottom: "3px" }}>
                          Formulario incremental
                        </div>
                        <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "11.5px", color: "#1e40af", lineHeight: 1.55 }}>
                          {pasoActivo === 1
                            ? "Configura los campos que verá el postulante en el primer paso."
                            : "El Paso 2 incluye automáticamente todos los campos del Paso 1. Aquí solo configuras los campos adicionales que se mostrarán al avanzar."}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Bulk actions */}
              <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", marginBottom: "14px" }}>
                <button
                  onClick={toggleAllEnabled}
                  style={{
                    padding: "5px 12px", borderRadius: "7px",
                    border: "1px solid #e2e8f0", background: "#fff", color: "#475569",
                    fontFamily: "Roboto, sans-serif", fontSize: "12px", fontWeight: 500,
                    cursor: "pointer", transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#0065DD"; (e.currentTarget as HTMLButtonElement).style.color = "#0065DD"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#e2e8f0"; (e.currentTarget as HTMLButtonElement).style.color = "#475569"; }}
                >
                  {allEnabled ? "Desmarcar todos" : "Marcar todos"}
                </button>
                <button
                  onClick={toggleAllRequired}
                  style={{
                    padding: "5px 12px", borderRadius: "7px",
                    border: "1px solid #e2e8f0", background: "#fff", color: "#475569",
                    fontFamily: "Roboto, sans-serif", fontSize: "12px", fontWeight: 500,
                    cursor: "pointer", transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#b45309"; (e.currentTarget as HTMLButtonElement).style.color = "#b45309"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#e2e8f0"; (e.currentTarget as HTMLButtonElement).style.color = "#475569"; }}
                >
                  {allRequired ? "Nada obligatorio" : "Todo obligatorio"}
                </button>
              </div>

              {/* Default fields notice */}
              <div
                style={{
                  marginBottom: "14px",
                  borderRadius: "9px",
                  background: "rgba(180,83,9,0.05)",
                  border: "1px solid rgba(180,83,9,0.18)",
                  overflow: "hidden",
                }}
              >
                <button
                  onClick={() => setShowDefaultFields((v) => !v)}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: "9px",
                    padding: "10px 12px", background: "transparent", border: "none",
                    cursor: "pointer", textAlign: "left", outline: "none",
                  }}
                >
                  <div
                    style={{
                      width: "16px", height: "16px", borderRadius: "50%",
                      background: "#b45309", color: "#fff",
                      fontSize: "10px", fontWeight: 700,
                      fontFamily: "Roboto, sans-serif",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    i
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "12px", fontWeight: 600, color: "#92400e" }}>
                      Hay campos obligatorios que no se muestran aquí
                    </div>
                    <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "11px", color: "#b45309", marginTop: "1px" }}>
                      {showDefaultFields ? "Ocultar lista" : "Ver lista de campos siempre obligatorios"}
                    </div>
                  </div>
                  <svg
                    width="14" height="14" viewBox="0 0 24 24"
                    fill="none" stroke="#b45309" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round"
                    style={{
                      flexShrink: 0,
                      transform: showDefaultFields ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                <AnimatePresence initial={false}>
                  {showDefaultFields && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      style={{ overflow: "hidden" }}
                    >
                      <div style={{ padding: "0 12px 12px 37px", fontFamily: "Roboto, sans-serif", fontSize: "11.5px", color: "#78350f", lineHeight: 1.6 }}>
                        <div style={{ marginBottom: "6px" }}>
                          Estos campos vienen por defecto en el formulario y siempre serán obligatorios para el postulante:
                        </div>
                        <ul style={{ margin: 0, paddingLeft: "18px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 16px" }}>
                          {CAMPOS_OBLIGATORIOS_DEFAULT.map((c) => (
                            <li key={c} style={{ color: "#78350f" }}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Campos */}
              <SectionHeader title="Campos" />
              <div style={{ display: "flex", flexDirection: "column", gap: "1px", marginBottom: "20px" }}>
                {CAMPOS.map((item: CampoItem) => {
                  const isOpcionCampo = item.key === "turno" || item.key === "cargo";
                  const showEditor =
                    isOpcionCampo &&
                    !lockedCampoKeys.has(item.key) &&
                    activeCampos[item.key].enabled;
                  return (
                    <React.Fragment key={item.key}>
                      <FieldRow
                        item={item}
                        state={activeCampos}
                        onToggle={activeToggleCampo}
                        lockedByLabel={lockedCampoKeys.has(item.key) ? otherStepLabel : undefined}
                      />
                      {showEditor && (
                        <MasterOptionsSelector
                          label={item.label}
                          catalog={catalogos[item.key as OpcionCampoKey]}
                          selected={seleccion[item.key as OpcionCampoKey]}
                          loading={loadingCatalogos}
                          adding={addingField === (item.key as OpcionCampoKey)}
                          error={
                            showOptionErrors &&
                            seleccion[item.key as OpcionCampoKey].length === 0
                          }
                          onToggle={(ci) => toggleOption(item.key as OpcionCampoKey, ci)}
                          onAdd={(name) => addOption(item.key as OpcionCampoKey, name)}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Documentos */}
              <SectionHeader title="Documentos" />
              <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                {DOCUMENTOS.map((item: CampoItem) => (
                  <FieldRow
                    key={item.key}
                    item={item}
                    state={activeDocumentos}
                    onToggle={activeToggleDocumento}
                    lockedByLabel={lockedDocKeys.has(item.key) ? otherStepLabel : undefined}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            background: "#fafafa",
          }}
        >
          <AnimatePresence>
            {saveError && (
              <motion.div
                key="save-error"
                initial={{ opacity: 0, y: -6, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -6, height: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                style={{
                  overflow: "hidden",
                  padding: "9px 14px",
                  borderRadius: "8px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12.5px",
                  color: "#b91c1c",
                }}
              >
                {saveError}
              </motion.div>
            )}
          </AnimatePresence>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button
              onClick={handleRequestClose}
              disabled={saving}
              style={{
                padding: "9px 20px",
                borderRadius: "9px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                color: "#475569",
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.6 : 1,
              }}
            >
              Cancelar
            </button>
            <motion.button
              whileHover={{ scale: saving || loadingConfig || !!loadError ? 1 : 1.02 }}
              whileTap={{ scale: saving || loadingConfig || !!loadError ? 1 : 0.97 }}
              onClick={handleGuardar}
              disabled={saving || loadingConfig || !!loadError}
              style={{
                padding: "9px 22px",
                borderRadius: "9px",
                border: "none",
                background: saving || loadingConfig || !!loadError
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #0050b3 0%, #0065DD 100%)",
                color: "#fff",
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                fontWeight: 600,
                cursor: saving || loadingConfig || !!loadError ? "not-allowed" : "pointer",
                boxShadow: saving || loadingConfig || !!loadError ? "none" : "0 2px 10px rgba(0,101,221,0.3)",
                transition: "background 0.2s, box-shadow 0.2s",
              }}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </motion.button>
          </div>
        </div>

        {/* ── Confirm close dialog ── */}
        {showConfirmClose && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15,23,42,0.45)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "18px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.93 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 320, damping: 24 }}
              style={{
                background: "#fff",
                borderRadius: "14px",
                padding: "28px 28px 22px",
                maxWidth: "360px",
                width: "90%",
                boxShadow: "0 16px 48px rgba(15,23,42,0.25)",
              }}
            >
              <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "15px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>
                ¿Descartar cambios?
              </div>
              <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#64748b", marginBottom: "22px", lineHeight: 1.55 }}>
                Tienes cambios sin guardar. Si cierras el formulario, se perderán todos los cambios realizados.
              </div>
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  onClick={() => setShowConfirmClose(false)}
                  style={{
                    padding: "8px 18px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    color: "#475569",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "13px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Seguir editando
                </button>
                <button
                  onClick={onClose}
                  style={{
                    padding: "8px 18px",
                    borderRadius: "8px",
                    border: "none",
                    background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                    color: "#fff",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(220,38,38,0.3)",
                  }}
                >
                  Descartar y cerrar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default EditarFormularioModal;
